import React, { useEffect, useState } from 'react';

import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, MenuItem, Tab, Tabs, TextField, Typography, useTheme } from '@mui/material';

import { IconArrowLeft, IconAdjustmentsAlt } from '@tabler/icons';

import api from '../../../../services/api';
import { useHistory, useLocation } from 'react-router-dom';
import FieldsSettingsPanel from './FieldsSettingsPanel';

const TABS = [
    {
        key: 'client',
        label: 'Clientes',
        apiBasePath: 'client_fields',
        entityLabel: 'Cliente',
        entityLabelPlural: 'clientes',
        entityDescription:
            'Crie campos customizados para os clientes cadastrados no CRM. Campos marcados como obrigatórios só passam a exigir preenchimento em novos cadastros — clientes já existentes não são afetados.'
    },
    {
        key: 'organization',
        label: 'Empresas',
        apiBasePath: 'organization_fields',
        entityLabel: 'Empresa',
        entityLabelPlural: 'organizações',
        entityDescription:
            'Crie campos customizados para as empresas cadastradas no CRM. Campos marcados como obrigatórios só passam a exigir preenchimento em novos cadastros — organizações já existentes não são afetadas.'
    },
    {
        key: 'deal',
        label: 'Negócios',
        entityLabel: 'Negócio',
        entityLabelPlural: 'negócios',
        entityDescription:
            'Crie campos customizados para os negócios de um pipeline específico — cada pipeline tem seus próprios campos.'
    },
    {
        key: 'product',
        label: 'Produtos',
        apiBasePath: 'item_fields',
        extraParams: { tipo: 'produto' },
        entityLabel: 'Produto',
        entityLabelPlural: 'produtos',
        entityDescription: 'Crie campos customizados para os produtos cadastrados no CRM.'
    },
    {
        key: 'event',
        label: 'Eventos',
        apiBasePath: 'event_fields',
        entityLabel: 'Evento',
        entityLabelPlural: 'eventos',
        entityDescription: 'Crie campos customizados para os eventos do calendário.'
    },
    {
        key: 'ticket',
        label: 'Tickets',
        apiBasePath: 'ticket_fields',
        entityLabel: 'Ticket',
        entityLabelPlural: 'tickets',
        entityDescription: 'Crie campos customizados para os tickets de suporte cadastrados no CRM.'
    },
    {
        key: 'servico',
        label: 'Serviços',
        apiBasePath: 'item_fields',
        extraParams: { tipo: 'servico' },
        entityLabel: 'Serviço',
        entityLabelPlural: 'serviços',
        entityDescription: 'Crie campos customizados para os serviços cadastrados no catálogo.'
    },
    {
        key: 'contrato',
        label: 'Contrato',
        apiBasePath: 'contrato_servico_fields',
        entityLabel: 'Contrato',
        entityLabelPlural: 'contratos',
        entityDescription: 'Crie campos personalizados para os Contratos de Serviço.'
    },
    {
        key: 'proposta',
        label: 'Proposta',
        apiBasePath: 'proposta_fields',
        entityLabel: 'Proposta',
        entityLabelPlural: 'propostas',
        entityDescription: 'Crie campos personalizados para as Propostas enviadas aos clientes. Arraste pra reordenar a posição no PDF.',
        sortable: true,
        supportsHideLabel: true,
        propostaExtras: true
    }
];

const CamposCustomizadosHub = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const history = useHistory();
    const location = useLocation();

    const [appId, setAppId] = useState(null);
    const [tab, setTab] = useState(() => {
        const tabKey = new URLSearchParams(location.search).get('tab');
        const index = TABS.findIndex((t) => t.key === tabKey);
        return index >= 0 ? index : 0;
    });
    const [pipelines, setPipelines] = useState([]);
    const [pipelineId, setPipelineId] = useState('');

    // Título fixo no topo do PDF da Proposta (Account.proposta_titulo_documento) -
    // só carregado/mostrado na aba Proposta, fica junto dos campos
    // customizados porque é configuração do mesmo documento.
    const [tituloDocumento, setTituloDocumento] = useState('');
    const [loadingTitulo, setLoadingTitulo] = useState(true);
    const [savingTitulo, setSavingTitulo] = useState(false);
    const [tituloStatus, setTituloStatus] = useState({ type: '', message: '' });

    useEffect(() => {
        api.get('v1/apps/default/')
            .then((r) => setAppId(r.data?.app?.id || null))
            .catch(console.error);
    }, []);

    useEffect(() => {
        if (!appId) return;
        api.get(`v1/apps/${appId}/pipelines/`)
            .then((r) => {
                const list = r.data || [];
                setPipelines(list);
                if (list.length > 0) setPipelineId(list[0].id);
            })
            .catch(console.error);
        api.get(`v1/apps/${appId}/account_settings/`)
            .then((r) => setTituloDocumento(r.data?.proposta_titulo_documento || ''))
            .catch(console.error)
            .finally(() => setLoadingTitulo(false));
    }, [appId]);

    const handleSalvarTitulo = async () => {
        setSavingTitulo(true);
        setTituloStatus({ type: '', message: '' });
        try {
            const res = await api.patch(`v1/apps/${appId}/account_settings/`, { proposta_titulo_documento: tituloDocumento });
            setTituloDocumento(res.data?.proposta_titulo_documento || '');
            setTituloStatus({ type: 'success', message: 'Título salvo!' });
        } catch (e) {
            setTituloStatus({ type: 'error', message: 'Erro ao salvar o título.' });
        } finally {
            setSavingTitulo(false);
        }
    };

    const currentTab = TABS[tab];
    const dealApiBasePath = pipelineId ? `pipelines/${pipelineId}/deal_fields` : null;

    return (
        <Box>
            <Button
                variant="outlined"
                startIcon={<IconArrowLeft size={18} />}
                onClick={() => history.goBack()}
                sx={{ borderRadius: '12px', textTransform: 'none' }}
            >
                Voltar
            </Button>

            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '30px',
                    marginTop: 2,
                    p: { xs: 3, md: 5 },
                    mb: 4,
                    background: isDark ? 'linear-gradient(135deg,#111827 0%,#0f172a 100%)' : 'linear-gradient(135deg,#f8fafc 0%,#ffffff 100%)',
                    border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(15,23,42,0.06)',
                    boxShadow: isDark ? '0 10px 40px rgba(0,0,0,0.35)' : '0 10px 40px rgba(15,23,42,0.06)'
                }}
            >
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                    <Chip icon={<IconAdjustmentsAlt size={14} />} label="Central Administrativa" sx={{ mb: 2, fontWeight: 700 }} />

                    <Typography
                        variant="h1"
                        sx={{
                            fontWeight: 900,
                            fontSize: { xs: '2rem', md: '3rem' },
                            letterSpacing: '-2px',
                            mb: 1,
                            background: 'linear-gradient(90deg,#2563eb,#7c3aed)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}
                    >
                        Campos Customizados
                    </Typography>

                    <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 700, lineHeight: 1.8 }}>
                        Defina campos extras pra cada tipo de registro do CRM — Clientes, Empresas, Negócios, Produtos e Eventos.
                    </Typography>
                </Box>
            </Box>

            <Tabs
                value={tab}
                onChange={(e, v) => setTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                    mb: 3,
                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, borderRadius: '12px 12px 0 0' }
                }}
            >
                {TABS.map((t) => (
                    <Tab key={t.key} label={t.label} />
                ))}
            </Tabs>

            {currentTab.key === 'proposta' && (
                <Card sx={{ borderRadius: '20px', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                            Título do documento
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                            Frase fixa no topo do PDF da Proposta (canto superior esquerdo, em negrito). Use uma quebra de linha se
                            quiser duas linhas, ex: "PROPOSTA" numa linha e "COMERCIAL" na outra.
                        </Typography>

                        {loadingTitulo ? (
                            <CircularProgress size={24} />
                        ) : (
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                <TextField
                                    value={tituloDocumento}
                                    onChange={(e) => setTituloDocumento(e.target.value)}
                                    multiline
                                    minRows={2}
                                    maxRows={2}
                                    sx={{ flex: 1, minWidth: 260, '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                                    placeholder={'PROPOSTA\nCOMERCIAL'}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleSalvarTitulo}
                                    disabled={savingTitulo}
                                    sx={{ borderRadius: '12px', textTransform: 'none', height: 56, px: 3 }}
                                >
                                    {savingTitulo ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Salvar'}
                                </Button>
                            </Box>
                        )}

                        {tituloStatus.message && (
                            <Alert severity={tituloStatus.type} sx={{ mt: 2, borderRadius: '14px' }} onClose={() => setTituloStatus({ type: '', message: '' })}>
                                {tituloStatus.message}
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            )}

            {currentTab.key === 'deal' && (
                <TextField
                    select
                    label="Pipeline"
                    value={pipelineId}
                    onChange={(e) => setPipelineId(e.target.value)}
                    sx={{ mb: 3, minWidth: 280, '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                >
                    {pipelines.length === 0 ? (
                        <MenuItem disabled>Nenhum pipeline encontrado</MenuItem>
                    ) : (
                        pipelines.map((p) => (
                            <MenuItem key={p.id} value={p.id}>
                                {p.name}
                            </MenuItem>
                        ))
                    )}
                </TextField>
            )}

            <FieldsSettingsPanel
                appId={appId}
                apiBasePath={currentTab.key === 'deal' ? dealApiBasePath : currentTab.apiBasePath}
                extraParams={currentTab.extraParams}
                entityLabel={currentTab.entityLabel}
                entityLabelPlural={currentTab.entityLabelPlural}
                entityDescription={currentTab.entityDescription}
                sortable={currentTab.sortable}
                supportsHideLabel={currentTab.supportsHideLabel}
                propostaExtras={currentTab.propostaExtras}
                key={currentTab.key === 'deal' ? `deal-${pipelineId}` : currentTab.key}
            />
        </Box>
    );
};

export default CamposCustomizadosHub;
